import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

export const STAGES = [
  { id: 'conversation', label: 'conversation', color: '#38bdf8' },
  { id: 'rate_confirmed', label: 'rate confirmed', color: '#a78bfa' },
  { id: 'contracted', label: 'contracted', color: '#d394ab' },
  { id: 'complete', label: 'complete', color: '#059669' },
  { id: 'declined', label: 'declined', color: '#94a3b8' },
];

export const DEAL_TYPES = ['Instagram Reel', 'TikTok Video', 'YouTube Integration', 'UGC Package', 'Event Appearance', 'Brand Ambassador', 'Podcast Sponsor'];

const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [talent, setTalent] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setDeals([]); setTalent([]); setBrands([]); setLoading(false); return; }
    setLoading(true);
    try {
      const [d, t, b] = await Promise.all([
        api.get('/deals'),
        api.get('/talent'),
        api.get('/brands'),
      ]);
      setDeals(d.data || []);
      setTalent(t.data || []);
      setBrands(b.data || []);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addDeal = async (deal) => {
    const payload = {
      title: deal.title, talent_id: deal.talent_id || null, brand_id: deal.brand_id || null,
      talent_ids: Array.isArray(deal.talent_ids) ? deal.talent_ids : (deal.talent_id ? [deal.talent_id] : []),
      talent_splits: deal.talent_splits || {},
      contact_id: deal.contact_id || null,
      type: deal.type, value: Number(deal.value) || 0, currency: deal.currency || 'GBP',
      stage: deal.stage || 'conversation', due_date: deal.due_date || null, go_live_date: deal.go_live_date || null,
      notes: deal.notes || '', link: deal.link || '',
      checklist: deal.checklist || {},
      commission_percent: deal.commission_percent ?? null,
      invoiced_at: deal.invoiced_at || '',
      paid_in_at: deal.paid_in_at || '',
      paid_out_at: deal.paid_out_at || '',
    };
    const { data } = await api.post('/deals', payload);
    setDeals((prev) => [data, ...prev]);
    return data;
  };

  const updateDeal = async (id, patch) => {
    const { data } = await api.patch(`/deals/${id}`, patch);
    setDeals((prev) => prev.map((d) => (d.id === id ? data : d)));
    return data;
  };

  const removeDeal = async (id) => {
    await api.delete(`/deals/${id}`);
    setDeals((prev) => prev.filter((d) => d.id !== id));
  };

  const moveDeal = (id, stage) => updateDeal(id, { stage });

  const addTalent = async (payload) => {
    const { data } = await api.post('/talent', payload);
    setTalent((prev) => [data, ...prev]);
    return data;
  };

  const updateTalent = async (id, patch) => {
    const { data } = await api.patch(`/talent/${id}`, patch);
    setTalent((prev) => prev.map((t) => (t.user_id === id ? data : t)));
  };

  const removeTalent = async (id) => {
    await api.delete(`/talent/${id}`);
    setTalent((prev) => prev.filter((t) => t.user_id !== id));
  };

  const addBrand = async (name, logo = '') => {
    const clean = (name || '').trim();
    if (!clean) throw new Error('Brand name required');
    const existing = brands.find((b) => (b.name || '').toLowerCase() === clean.toLowerCase());
    if (existing) return existing;
    const { data } = await api.post('/brands', { name: clean, logo });
    setBrands((prev) => (prev.find((b) => b.id === data.id) ? prev : [...prev, data]));
    return data;
  };

  const updateBrand = async (id, patch) => {
    const { data } = await api.patch(`/brands/${id}`, patch);
    setBrands((prev) => prev.map((b) => (b.id === id ? data : b)));
    return data;
  };

  const removeBrand = async (id) => {
    await api.delete(`/brands/${id}`);
    setBrands((prev) => prev.filter((b) => b.id !== id));
    // clear brand from local deals cache
    setDeals((prev) => prev.map((d) => (d.brand_id === id ? { ...d, brand_id: null, contact_id: null } : d)));
  };

  const talentMap = useMemo(() => Object.fromEntries(talent.map((t) => [t.user_id, t])), [talent]);
  const brandMap = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);

  return (
    <DataContext.Provider value={{
      loading, deals, talent, brands, stages: STAGES,
      addDeal, updateDeal, removeDeal, moveDeal,
      addTalent, updateTalent, removeTalent,
      addBrand, updateBrand, removeBrand,
      talentMap, brandMap, refresh,
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const c = useContext(DataContext);
  if (!c) throw new Error('useData must be used within DataProvider');
  return c;
};

// Back-compat alias so existing components keep working
export const useDeals = useData;
